import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Select } from '../ui/newSelect';
import { Actions as CONSULTANT_ACTIONS } from '../../store/actions/consultantClientsActions';
import { toggleAlertMessage } from '../../store/action';
import {
  friendlyConsultantClientError,
  resolveConsultantCatalogPrice,
} from '../utils/consultantClientUtils';

const money = (value) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(Number(value) || 0);

const AmendConsultantClientModal = ({
  open,
  client,
  schedule,
  programme,
  catalog,
  onClose,
  onSuccess,
  amendConsultantClient,
  showAlert,
}) => {
  const [form, setForm] = useState({
    planType: 'catalog_plan',
    planId: 'measure',
    fteBand: '1-10',
    payMode: 'monthly_cycle',
    termMonths: 12,
    autoRenew: true,
    billingFrequency: 'annual',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      planType: 'catalog_plan',
      planId: schedule?.planId || 'measure',
      fteBand: schedule?.fteBand || '1-10',
      payMode: schedule?.payMode || 'monthly_cycle',
      termMonths: Number(schedule?.termMonths) === 36 ? 36 : 12,
      autoRenew: schedule?.autoRenew !== false,
      billingFrequency: schedule?.payMode === 'pay_in_full' ? 'annual' : 'monthly',
    });
  }, [open, schedule]);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const pricing = useMemo(
    () => resolveConsultantCatalogPrice(catalog, programme, form.planId, form.fteBand, form.termMonths),
    [catalog, programme, form.planId, form.fteBand, form.termMonths],
  );

  const handleSubmit = async () => {
    if (!client?.id) return;
    setSaving(true);
    const body = {
      planType: form.planType,
      payMode: form.payMode,
      termMonths: Number(form.termMonths) || 12,
      autoRenew: !!form.autoRenew,
      billingFrequency: form.payMode === 'pay_in_full' ? 'annual' : 'monthly',
    };
    body.planId = form.planId;
    body.fteBand = form.fteBand;

    const result = await amendConsultantClient(client.id, body);
    setSaving(false);

    if (result.ok) {
      showAlert(true, 'Amendment order form created.', 'success', 5000);
      if (result.data?.agreementUrl) window.open(result.data.agreementUrl, '_blank', 'noopener');
      onSuccess?.(result.data);
    } else {
      showAlert(true, friendlyConsultantClientError(result.error || result.message), 'error', 6000);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className='bg-card text-card-foreground border-border max-w-lg'>
        <DialogHeader>
          <DialogTitle>Change plan — {client?.name || client?.accountName}</DialogTitle>
        </DialogHeader>

        <DialogBody className='space-y-5'>
          <div>
            <Label>Plan type</Label>
            <Select
              value={form.planType}
              onValueChange={(v) => update('planType', v)}
              options={[
                { value: 'catalog_plan', label: 'Plan × FTE' },
              ]}
            />
          </div>
          <div className='grid grid-cols-2 gap-3'>
            <div>
              <Label>Plan</Label>
              <Select
                value={form.planId}
                onValueChange={(value) => update('planId', value)}
                options={(catalog?.plans || ['discover', 'measure', 'execute', 'coordinate', 'govern']).map((value) => ({
                  value,
                  label: value.replace(/\b\w/g, (letter) => letter.toUpperCase()),
                }))}
              />
            </div>
            <div>
              <Label>Client FTE band</Label>
              <Select
                value={form.fteBand}
                onValueChange={(value) => update('fteBand', value)}
                options={(catalog?.fteBands || ['1-10', '11-50', '51-100', '101-200', '201-250', '250-plus']).map((value) => ({
                  value,
                  label: `${value} FTE`,
                }))}
              />
            </div>
          </div>
          <div className='grid grid-cols-2 gap-3'>
            <div>
              <Label>Pay mode</Label>
              <Select
                value={form.payMode}
                onValueChange={(v) => update('payMode', v)}
                options={[
                  { value: 'monthly_cycle', label: 'Monthly invoicing' },
                  { value: 'pay_in_full', label: 'Pay in full' },
                ]}
              />
            </div>
            <div>
              <Label>Term</Label>
              <Select
                value={form.termMonths}
                onValueChange={(value) => update('termMonths', Number(value))}
                options={[
                  { value: 12, label: '12 months' },
                  { value: 36, label: '36 months (extra 5%)' },
                ]}
              />
            </div>
          </div>
          {pricing && (
            <div className='rounded-lg border border-border bg-muted/40 p-4'>
              <div className='flex items-start justify-between gap-4'>
                <div>
                  <p className='text-xs text-muted-foreground'>Annual list price</p>
                  <p className='mt-1 text-sm text-muted-foreground line-through'>{money(pricing.standard)}</p>
                </div>
                <div className='flex flex-wrap justify-end gap-1.5'>
                  <Badge variant='secondary'>{pricing.programmeDiscountPercent}% consultant discount</Badge>
                  {pricing.commitmentDiscountPercent > 0 && (
                    <Badge variant='secondary'>{pricing.commitmentDiscountPercent}% term discount</Badge>
                  )}
                </div>
              </div>
              <div className='mt-3 flex items-end justify-between gap-4 border-t border-border pt-3'>
                <div>
                  <p className='text-xs text-muted-foreground'>Net price you pay</p>
                  <p className='text-xl font-semibold text-foreground'>{money(pricing.net)} / year</p>
                  {form.payMode === 'monthly_cycle' && (
                    <p className='text-xs text-muted-foreground'>{money(pricing.monthly)} invoiced monthly</p>
                  )}
                </div>
                <p className='text-sm font-medium text-success'>You save {money(pricing.savings)} / year</p>
              </div>
            </div>
          )}
          <p className='text-xs text-muted-foreground'>
            Creates an amendment order form. You or the client must accept it before this price replaces the current
            schedule. No retrospective charge is raised.
          </p>
        </DialogBody>

        <DialogFooter className='gap-2'>
          <Button variant='outline' onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button loading={saving} onClick={handleSubmit}>
            Create amendment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const mapDispatchToProps = (dispatch) => ({
  amendConsultantClient: (orgId, body) => dispatch(CONSULTANT_ACTIONS.amendConsultantClient(orgId, body)),
  showAlert: (status, message, severity, hideDuration) =>
    dispatch(toggleAlertMessage(status, message, severity, hideDuration)),
});

export default connect(null, mapDispatchToProps)(AmendConsultantClientModal);
